import * as React from 'react';
import { chatService } from '@/services/chatService';

interface UseMessageAudioReturn {
  audioBuffer: string[] | null;
  isLoading: boolean;
  error: string | null;
  isCorsError: boolean;
}

/**
 * Hook to fetch and manage audio data for a message.
 * It checks if audio is available and not yet buffered, then fetches it.
 */
export const useMessageAudio = (
  audioUrl: string | undefined,
  existingBuffer: string[] | undefined
): UseMessageAudioReturn => {
  const [audioBuffer, setAudioBuffer] = React.useState<string[] | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isCorsError, setIsCorsError] = React.useState(false);

  React.useEffect(() => {
    // If we already have a buffer from streaming, use it
    if (existingBuffer && existingBuffer.length > 0) {
      return;
    }

    // If no URL is provided, there is nothing to fetch
    if (!audioUrl) {
      return;
    }

    let isMounted = true;

    const fetchAudio = async () => {
      setIsLoading(true);
      setError(null);
      setIsCorsError(false);
      try {
        const base64Audio = await chatService.fetchMessageAudio(audioUrl);
        if (isMounted) {
          setAudioBuffer([base64Audio]);
        }
      } catch (err) {
        if (isMounted) {
          // Log only critical errors, remove verbose debugging
          const msg = err instanceof Error ? err.message : 'Unknown error';
          setError(msg);

          // Detect typical CORS failure signature (TypeError: Failed to fetch)
          if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
            setIsCorsError(true);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAudio();

    return () => {
      isMounted = false;
    };
  }, [audioUrl, existingBuffer]);

  // Return either the locally fetched buffer or the existing one passed in
  return {
    audioBuffer: existingBuffer && existingBuffer.length > 0 ? existingBuffer : audioBuffer,
    isLoading,
    error,
    isCorsError,
  };
};
